import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@phosphor-icons/react";
import { DAYS, BLOCKS } from "@/lib/slots";
import { SELF_QUAL_OPTIONS } from "./constants";

export default function SignupStep2Contact({
  form, setForm,
  availability, selectedSlots, toggleSlot,
  waitingList, onToggleWaitingList, anyOpen,
  selfQuals, toggleQual,
  onBack, onContinue,
}) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">Business / Company Name *</Label>
          <Input data-testid="signup-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 bg-white border-cvc-border" />
        </div>
        <div>
          <Label className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">Your Name *</Label>
          <Input data-testid="signup-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1.5 bg-white border-cvc-border" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={20} weight="duotone" className="text-cvc-navy" />
            <h4 className="font-heading text-xl text-cvc-navy">Preferred meeting windows</h4>
          </div>
          <p className="font-body text-xs text-cvc-ink-soft">Pick any that work — Jonathan confirms one.</p>
        </div>

        {!anyOpen ? (
          <div className="mt-4 rounded-2xl border-2 border-dashed border-cvc-border bg-white p-6 text-center">
            <p className="font-heading text-2xl text-cvc-navy">Calendar is currently full.</p>
            <p className="font-body text-sm text-cvc-ink-soft mt-2">No problem — we can put you on the soonest-next-availability list and reach out the moment a slot opens.</p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-separate border-spacing-1.5">
              <thead>
                <tr>
                  <th className="text-left"></th>
                  {DAYS.map((d) => (
                    <th key={d.key} className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft text-center pb-1">{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BLOCKS.map((b) => (
                  <tr key={b.key}>
                    <td className="text-right pr-3">
                      <div className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">{b.label}</div>
                      <div className="font-body text-[10px] text-cvc-ink-soft/70">{b.hint}</div>
                    </td>
                    {DAYS.map((d) => {
                      const k = `${d.key}-${b.key}`;
                      const open = !!availability[k];
                      const selected = selectedSlots.includes(k);
                      return (
                        <td key={k} className="p-0">
                          <button
                            type="button"
                            disabled={!open}
                            data-testid={`slot-${k}`}
                            onClick={() => toggleSlot(k)}
                            className={`w-full h-12 rounded-lg border text-xs font-body transition-all ${
                              !open
                                ? "bg-cvc-parchment/50 border-transparent text-cvc-ink-soft/40 cursor-not-allowed"
                                : selected
                                ? "bg-cvc-navy border-cvc-navy text-cvc-cream"
                                : "bg-white border-cvc-border text-cvc-ink hover:border-cvc-navy"
                            }`}
                          >
                            {!open ? "—" : selected ? "Selected" : "Available"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-start gap-3">
          <Checkbox id="waiting" data-testid="signup-waiting-list" checked={waitingList} onCheckedChange={onToggleWaitingList} />
          <Label htmlFor="waiting" className="font-body text-sm text-cvc-ink cursor-pointer">
            Put me on the soonest-next-availability waiting list{!anyOpen && " (only option right now)"}.
          </Label>
        </div>
      </div>

      {/* Optional self-qualification checkboxes — not required, no validation */}
      <div className="px-1" data-testid="self-quals">
        <p className="font-body text-[11px] uppercase tracking-[0.2em] text-cvc-ink-soft">Optional · Self-qualification</p>
        <p className="font-body text-xs text-cvc-ink-soft/85 mt-1 italic">
          Don't tick every box? You can still sign up — Jonathan reviews each case and may suggest some preparations before bringing you on as a client.
        </p>
        <ul className="mt-3 space-y-2">
          {SELF_QUAL_OPTIONS.map((q) => (
            <li key={q.id} className="flex items-start gap-2.5">
              <Checkbox
                id={`qual-${q.id}`}
                data-testid={`qual-${q.id}`}
                checked={selfQuals.includes(q.id)}
                onCheckedChange={() => toggleQual(q.id)}
                className="mt-0.5 border-cvc-border data-[state=checked]:bg-scaled-green data-[state=checked]:border-scaled-green"
              />
              <Label
                htmlFor={`qual-${q.id}`}
                className="font-body text-[13px] text-cvc-ink-soft leading-snug cursor-pointer"
              >
                {q.label}
              </Label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <Label className="font-body text-xs uppercase tracking-wider text-cvc-ink-soft">Anything else Jonathan should know?</Label>
        <Textarea data-testid="signup-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Number of employees, profit margin, biggest current bottleneck…" className="mt-1.5 bg-white border-cvc-border min-h-[68px]" />
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        {onBack ? (
          <Button variant="ghost" data-testid="signup-step2-back" onClick={onBack} className="text-cvc-ink-soft">← Back</Button>
        ) : <span />}
        <div className="flex items-center gap-4 flex-wrap ml-auto">
          <p className="font-body text-xs text-cvc-ink-soft italic">Jonathan may require meeting before accepting your case. Payment is not processed until you're accepted as a client.</p>
          <Button data-testid="signup-step2-next" onClick={onContinue} className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-8 py-5 h-auto">
            Continue
          </Button>
        </div>
      </div>
    </>
  );
}
